/* eslint-disable n/no-callback-literal */
import { logException } from './Utils';
import { getIdToken } from './Firebase';

let requestQueue = [];
let onlineListenerInited = false;
const cachedPromises = {};

const resolver = (resolve, request, params) => {
  try {
    resolve(JSON.parse(request.responseText));
  } catch (ex) {
    logException(ex, {
      requestUrl: request.responseURL,
      requestParams: params ? JSON.stringify(params) : null,
      responseText: request.responseText
    });
    resolve(request.responseText);
  }
};

const rejector = (reject, request, params) => {
  try {
    reject(JSON.parse(request.responseText));
  } catch (ex) {
    logException(ex, {
      requestUrl: request.responseURL,
      requestParams: params || null,
      responseText: request.responseText
    });
    reject(request.responseText);
  }
};

export default function httpRequest ({ method, url, params, headers, requestUpdated }) {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(method, url);

    if ('onload' in request && 'onerror' in request) {
      request.onload = () => {
        if (request.status === 200) {
          resolver(resolve, request, params);
        } else {
          rejector(reject, request, params);
        }
      };

      request.onerror = () => {
        rejector(reject, request);
      };

      if (requestUpdated) {
        requestUpdated.cancel = function () {
          request.abort(); // abort request
          reject(new Error('Cancelled'));
        };
      }
    } else {
      request.onreadystatechange = () => {
        if (request.readyState === XMLHttpRequest.DONE) {
          if (request.status === 200) {
            resolver(resolve, request, params);
          } else {
            rejector(reject, request, params);
          }
        }
      };
    }

    request.setRequestHeader('content-type', 'application/json');

    Object.keys(headers || {}).forEach((key) => {
      request.setRequestHeader(key, headers[key]);
    });

    if (params) {
      request.send(JSON.stringify(params));
    } else {
      request.send();
    }
  });
}

export const authHttpRequest = ({ method, url, params, headers, requestUpdated }) =>
  getIdToken().then((token) =>
    httpRequest({
      method,
      url,
      params,
      headers: {
        ...(headers || {}),
        Authorization: token
      },
      requestUpdated
    })
  );

const isOnline = (callback) => {
  const request = new XMLHttpRequest();
  request.open('GET', '/favicon.ico');

  if ('onload' in request && 'onerror' in request) {
    request.onload = () => {
      callback(null);
    };

    request.onerror = () => {
      callback('OFFLINE');
    };
  } else {
    request.onreadystatechange = () => {
      if (request.readyState === XMLHttpRequest.DONE) {
        if (request.status === 0) {
          callback('OFFLINE');
        } else {
          callback(null);
        }
      }
    };
  }

  request.send();
};

const onlineListener = () => {
  isOnline((err) => {
    if (!err) {
      onlineListenerInited = false;

      requestQueue.forEach(({ options, callback }) => {
        makeQueuedRequest(options, callback);
      });

      requestQueue = [];
      return;
    }

    setTimeout(() => {
      onlineListener();
    }, 2000);
  });
};

const initOnlineListener = () => {
  if (onlineListenerInited) {
    return;
  }

  onlineListenerInited = true;
  onlineListener();
};

export const makeQueuedRequest = (options, callback) => {
  const request = new XMLHttpRequest();
  request.open(options.method, options.url);

  callback = callback || {};
  const onSuccess = callback.onSuccess || (() => {});
  const onError = callback.onError || (() => {});

  if ('ontimeout' in request) {
    request.ontimeout = () => {
      // TIMEOUT
      requestQueue.push({
        options,
        callback
      });
      initOnlineListener();
    };
  }

  if ('onload' in request && 'onerror' in request) {
    request.onload = () => {
      if (request.status === 200) {
        onSuccess(request);
      } else {
        onError(request);
      }
    };

    request.onerror = () => {
      // OFFLINE
      requestQueue.push({
        options,
        callback
      });
      initOnlineListener();
    };
  } else {
    request.onreadystatechange = () => {
      if (request.readyState === XMLHttpRequest.DONE) {
        if (request.status === 200) {
          onSuccess(request);
        } else if (request.status === 0) {
          // OFFLINE
          requestQueue.push({
            options,
            callback
          });
          initOnlineListener();
        } else {
          onError(request);
        }
      }
    };
  }

  request.setRequestHeader('content-type', 'application/json');

  if (options.params) {
    request.send(JSON.stringify(options.params));
  } else {
    request.send();
  }
};

export const createQueryParams = (queryParamsData) => {
  if (!Array.isArray(queryParamsData)) {
    return '';
  }

  const queryParams = queryParamsData.reduce((cumulativeArray, { key, value }) => {
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        value = JSON.stringify(value);
      }
      cumulativeArray.push(`${key}=${encodeURIComponent(value)}`);
    }
    return cumulativeArray;
  }, []);

  if (queryParams.length > 0) {
    return `?${queryParams.join('&')}`;
  }
  return '';
};

export const makeRequest = ({ promiseCacheKey, apiFunc, ...apiParams }) => {
  if (cachedPromises[promiseCacheKey]) {
    return cachedPromises[promiseCacheKey];
  }

  cachedPromises[promiseCacheKey] = new Promise((resolve, reject) => {
    apiFunc(apiParams)
      .then((response) => {
        resolve(response);
        delete cachedPromises[promiseCacheKey];
      })
      .catch((err) => {
        reject(err);
        delete cachedPromises[promiseCacheKey];
      });
  });

  return cachedPromises[promiseCacheKey];
};
